<template>
  <div class="schema-by-page">
    <div v-if="legendEntries.length" class="verdict-legend">
      <div
        v-for="entry in legendEntries"
        :key="entry.label"
        :class="['verdict-legend-row', `accent-${entry.tone}`]"
      >
        <span :class="['chip', `chip-${entry.tone}`]">{{ entry.label }}</span>
        <span class="verdict-legend-desc">{{ entry.desc }}</span>
      </div>
    </div>

    <p v-if="!pages.length" class="finding-note">
      No page-level schema diagnostics were returned for this finding.
    </p>

    <template v-else>
      <div class="schema-summary">
        <div class="schema-summary-counts">
          {{ stats.pageCount }} {{ stats.pageCount === 1 ? 'page needs' : 'pages need' }} fixes ·
          {{ stats.issueCount }} {{ stats.issueCount === 1 ? 'issue' : 'issues' }} in total
          <span v-if="stats.notAssessedCount" class="schema-summary-muted">
            · {{ stats.notAssessedCount }} not assessed
          </span>
        </div>
        <button type="button" class="schema-toggle-all" @click="toggleAll">
          {{ allExpanded ? 'Collapse all' : 'Expand all' }}
        </button>
      </div>

      <div class="schema-page-list">
        <div
          v-for="(page, index) in pages"
          :key="pageKey(page, index)"
          :class="['schema-page', `accent-${verdictMetaFor(page.verdict).tone}`]"
        >
          <button type="button" class="schema-page-head" @click="togglePage(pageKey(page, index))">
            <v-icon size="16" class="schema-page-chevron">
              {{ isExpanded(pageKey(page, index)) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
            </v-icon>
            <span :class="['chip', `chip-${verdictMetaFor(page.verdict).tone}`]">
              {{ verdictMetaFor(page.verdict).label }}
            </span>
            <span class="schema-page-path">{{ pagePath(page.url) }}</span>
            <span class="schema-page-intent">{{ normalizeLabel(page.page_intent) }}</span>
            <v-chip
              v-if="page.severity"
              size="x-small"
              :color="severityColor(page.severity)"
              variant="tonal"
            >{{ normalizeLabel(page.severity) }}</v-chip>
            <span v-if="issuesFor(page).length" class="schema-page-count">
              {{ issuesFor(page).length }} {{ issuesFor(page).length === 1 ? 'issue' : 'issues' }}
            </span>
          </button>

          <div v-if="isExpanded(pageKey(page, index))" class="schema-page-body">
            <div class="schema-meta-row">
              <span class="schema-meta-label">Page URL</span>
              <a
                v-if="isHttpUrl(page.url)"
                :href="page.url"
                target="_blank"
                rel="noopener"
                class="finding-url"
              >{{ page.url }}</a>
              <span v-else class="schema-meta-empty">{{ page.url || 'Not recorded' }}</span>
            </div>
            <div class="schema-meta-row">
              <span class="schema-meta-label">Canonical</span>
              <a
                v-if="isHttpUrl(page.canonical_url)"
                :href="page.canonical_url"
                target="_blank"
                rel="noopener"
                class="finding-url"
              >{{ page.canonical_url }}</a>
              <span v-else class="schema-meta-empty">None declared</span>
            </div>

            <DiagnosisFieldBlock
              kind="chips"
              label="Declared types"
              :items="page.declared_types"
              tone="neutral"
              mono
            />

            <p v-if="!issuesFor(page).length" class="finding-note">
              {{ verdictMetaFor(page.verdict).desc }}
            </p>

            <template v-else>
              <div class="finding-field-label schema-issues-label">
                The {{ issuesFor(page).length }} {{ issuesFor(page).length === 1 ? 'issue' : 'issues' }}
              </div>
              <div
                v-for="(issue, issueIndex) in issuesFor(page)"
                :key="`${pageKey(page, index)}-issue-${issueIndex}`"
                class="schema-issue"
              >
                <div class="schema-issue-head">
                  <span class="chip chip-neutral chip-mono">{{ issue.issue_type }}</span>
                  <div v-if="evidenceRefs(issue).tokens.length" class="schema-issue-from">
                    <span class="schema-issue-from-label">from</span>
                    <span
                      v-for="token in evidenceRefs(issue).tokens"
                      :key="token"
                      class="chip chip-neutral chip-mono"
                    >{{ token }}</span>
                  </div>
                </div>
                <div class="two-col schema-issue-states">
                  <div class="schema-issue-observed">
                    <div class="finding-field-label">What the markup does</div>
                    <p class="schema-issue-text">{{ issue.observed_state }}</p>
                  </div>
                  <div class="schema-issue-expected">
                    <div class="finding-field-label">What it should do</div>
                    <p class="schema-issue-text">{{ issue.expected_state }}</p>
                  </div>
                </div>
                <DiagnosisFieldBlock
                  kind="urls"
                  label="Evidence"
                  :items="evidenceRefs(issue).urls"
                  boxed
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import DiagnosisFieldBlock from './DiagnosisFieldBlock.vue'
import {
  isHttpUrl,
  normalizeLabel,
  pagePath,
  schemaPageIssues,
  schemaPageStats,
  schemaPagesToShow,
  severityColor,
  splitEvidenceRefs,
  verdictMetaFor
} from '@/utils/diagnosisFindings'

export default {
  name: 'DiagnosisSchemaByPage',
  components: { DiagnosisFieldBlock },
  props: {
    finding: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      expandedPages: {}
    }
  },
  computed: {
    pages() {
      return schemaPagesToShow(this.finding)
    },
    stats() {
      return schemaPageStats(this.pages)
    },
    /** Only the verdicts actually on screen, so the legend never explains a row that is not there. */
    legendEntries() {
      const seen = new Set()
      return this.pages.reduce((entries, page) => {
        const meta = verdictMetaFor(page.verdict)
        if (!meta.desc || seen.has(meta.label)) return entries
        seen.add(meta.label)
        entries.push(meta)
        return entries
      }, [])
    },
    allExpanded() {
      return this.pages.length > 0
        && this.pages.every((page, index) => this.expandedPages[this.pageKey(page, index)])
    }
  },
  watch: {
    finding: {
      immediate: true,
      handler() {
        const first = this.pages[0]
        this.expandedPages = first ? { [this.pageKey(first, 0)]: true } : {}
      }
    }
  },
  methods: {
    isHttpUrl,
    normalizeLabel,
    pagePath,
    severityColor,
    verdictMetaFor,
    issuesFor(page) {
      return schemaPageIssues(page)
    },
    evidenceRefs(issue) {
      return splitEvidenceRefs(issue?.evidence_refs)
    },
    pageKey(page, index) {
      return `${index}-${page?.url || 'page'}`
    },
    isExpanded(key) {
      return !!this.expandedPages[key]
    },
    togglePage(key) {
      this.expandedPages = {
        ...this.expandedPages,
        [key]: !this.expandedPages[key]
      }
    },
    toggleAll() {
      if (this.allExpanded) {
        this.expandedPages = {}
        return
      }
      this.expandedPages = this.pages.reduce((state, page, index) => {
        state[this.pageKey(page, index)] = true
        return state
      }, {})
    }
  }
}
</script>

<style scoped>
.schema-by-page {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.verdict-legend {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  border: 1px solid var(--brand-sand-300);
  border-radius: 10px;
  background: var(--brand-sand-50);
  padding: 12px 14px;
}

.verdict-legend-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  border-left: 3px solid transparent;
  padding-left: 10px;
}

.verdict-legend-desc {
  font-size: 12.5px;
  color: var(--brand-ink-800);
  line-height: 1.45;
}

.schema-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.schema-summary-counts {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--brand-ink-800);
}

.schema-summary-muted {
  font-weight: 500;
  opacity: 0.7;
}

.schema-toggle-all {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--brand-ocean-600);
  cursor: pointer;
}

.schema-page-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.schema-page {
  border: 1px solid var(--brand-sand-300);
  border-left-width: 3px;
  border-left-color: transparent;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

/* One accent scale drives the legend, the rows and the issue cards. */
.accent-negative {
  border-left-color: #c0554f;
}

.accent-warn {
  border-left-color: var(--brand-amber-400);
}

.accent-neutral {
  border-left-color: var(--brand-sand-300);
}

.accent-positive {
  border-left-color: var(--brand-teal-500);
}

.accent-info {
  border-left-color: var(--brand-ocean-400);
}

.schema-page-head {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;
}

.schema-page-chevron {
  flex-shrink: 0;
  color: var(--brand-ink-800);
}

.schema-page-path {
  flex: 1;
  min-width: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  color: var(--brand-ink-950);
  overflow-wrap: anywhere;
}

.schema-page-intent,
.schema-page-count {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--brand-ink-800);
}

.schema-page-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-top: 1px solid var(--brand-sand-300);
  padding: 14px 12px;
}

.schema-meta-row {
  display: flex;
  gap: 12px;
  font-size: 12.5px;
}

.schema-meta-label {
  flex-shrink: 0;
  min-width: 96px;
  font-weight: 600;
  color: var(--brand-ink-800);
}

.schema-meta-empty {
  color: var(--brand-ink-800);
  opacity: 0.7;
}

.schema-issues-label {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 4px 0 0;
}

.schema-issue {
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid var(--brand-sand-300);
  border-radius: 10px;
  background: var(--brand-sand-50);
  padding: 12px 14px;
}

.schema-issue-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.schema-issue-from {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.schema-issue-from-label {
  font-size: 11px;
  color: var(--brand-ink-800);
  opacity: 0.7;
}

.schema-issue-states {
  align-items: start;
}

.schema-issue-observed .finding-field-label {
  color: #9b2c2c;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.schema-issue-expected {
  border-left: 1px solid var(--brand-sand-300);
  padding-left: 16px;
}

.schema-issue-expected .finding-field-label {
  color: #127a6a;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.schema-issue-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--brand-ink-950);
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.finding-field-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--brand-ink-800);
  margin-bottom: 6px;
}

.finding-note {
  margin: 0;
  font-size: 13px;
  color: var(--brand-ink-800);
  line-height: 1.5;
}

.finding-url {
  color: var(--brand-ocean-600);
  font-size: 12.5px;
  word-break: break-all;
}

.chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.chip-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
}

.chip-info {
  background: var(--brand-ocean-100);
  color: var(--brand-ocean-600);
}

.chip-positive {
  background: var(--brand-teal-100);
  color: #127a6a;
}

.chip-negative {
  background: var(--brand-weak-100);
  color: #9b2c2c;
}

.chip-warn {
  background: var(--brand-amber-100);
  color: #92610a;
}

.chip-neutral {
  background: var(--brand-sand-100);
  color: var(--brand-ink-800);
}

@media (max-width: 800px) {
  .two-col,
  .verdict-legend {
    grid-template-columns: 1fr;
  }

  .schema-issue-expected {
    border-left: 0;
    border-top: 1px solid var(--brand-sand-300);
    padding-left: 0;
    padding-top: 12px;
  }

  .schema-page-head {
    flex-wrap: wrap;
  }
}
</style>
